import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactSwitch from 'react-switch';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  IconButton,
  Tooltip
} from '@mui/material';
import { Lock as LockIcon, LockOpen as LockOpenIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import tagService from './services/tagService';
import { getCustomWidgetDefinition } from './widgets';

const SimplePropertyInspector = ({ widget, onChange, onDelete, onToggleLock, customWidgetDefs = [] }) => {
  const [properties, setProperties] = useState({});
  const [availableTags, setAvailableTags] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  
  // Local state for ComboBox options string editing
  const [optionsStringLocal, setOptionsStringLocal] = useState('');
  
  // Local state for TrendChart tagIds to handle async updates
  const [trendTagIdsLocal, setTrendTagIdsLocal] = useState(null);
  
  // Image widget upload states
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const fileInputRef = useRef(null);

  // Load tags and pages on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load tags
        const tags = await tagService.getTags();
        setAvailableTags(tags || []);

        // Load SCADA pages
        const pages = await tagService.getSCADAPages();
        // Filter out current page if we know it
        const currentPath = window.location.pathname;
        const filteredPages = pages.filter(page => page.path !== currentPath);
        setAvailablePages(filteredPages);
      } catch (error) {
        console.error('🔧 Error loading tags/pages:', error);
        setAvailableTags([]);
        setAvailablePages([]);
      }
    };
    loadData();
  }, []);

  // Initialize properties when widget changes
  useEffect(() => {
    if (widget) {
      // Structural/meta keys that should NOT be treated as editable properties
      const structuralKeys = new Set([
        'id', 'type', 'component', 'x', 'y', 'width', 'height',
        'zIndex', 'locked', 'properties', 'props', 'elementDetails',
        'isContainer', 'containerId', 'customComponents', 'name', 'groupId'
      ]);
      const nestedProps = widget.properties || widget.props || {};
      const widgetProps = {
        // Layout properties
        x: widget.x || 0,
        y: widget.y || 0,
        width: widget.width || 100,
        height: widget.height || 50,
        // Widget specific properties
        ...nestedProps,
      };

      // Pick up ALL top-level widget properties (text, label, actionType, etc.)
      // that are missing from nested properties
      Object.keys(widget).forEach(key => {
        if (!structuralKeys.has(key) && widget[key] !== undefined && widgetProps[key] === undefined) {
          widgetProps[key] = widget[key];
        }
      });


      // Ensure tagIds is always an array for TrendChart
      if ((widget?.type === 'TrendChart' || widget?.component === 'TrendChart' || widget?.type === 'trend-chart') && !widgetProps.tagIds) {
        widgetProps.tagIds = [];
      }

      setProperties(widgetProps);

      // Initialize local options string for ComboBox and ButtonMenu
      if (widget?.type === 'ComboBox' || widget?.component === 'ComboBox' || widget?.type === 'combo-box' ||
          widget?.type === 'ButtonMenu' || widget?.component === 'ButtonMenu' || widget?.type === 'button-menu') {
        setOptionsStringLocal(JSON.stringify(widgetProps.options || [], null, 2));
      }

      // Reset TrendChart local tagIds when widget changes
      setTrendTagIdsLocal(null);
    } else {
      setProperties({});
      setOptionsStringLocal('');
      setTrendTagIdsLocal(null);
    }
  }, [widget?.id]);

  // Update property and immediately notify parent
  const updateProperty = (key, value) => {
    const newProps = { ...properties, [key]: value };
    setProperties(newProps);
    
    if (widget && onChange) {
      // Separate layout props from widget props
      const { x, y, width, height, ...widgetProps } = newProps;
      
      const updatedWidget = {
        id: widget.id,
        x: x,
        y: y,
        width: width,
        height: height,
        properties: widgetProps,
        props: widgetProps
      };
      
      onChange(updatedWidget);
    } else {
    }
  };

  // Image upload functionality
  const handleImageUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadStatus('Yükleniyor...');
    
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch('/api/scada/upload-image', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        const newSrc = result.filename;
        
        // Update the src property
        updateProperty('src', newSrc);
        setUploadStatus(`✅ Yüklendi: ${file.name}`);
        
      } else {
        const errorText = await response.text();
        setUploadStatus(`❌ Yükleme başarısız: ${errorText}`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadStatus(`❌ Hata: ${error.message}`);
    } finally {
      setUploading(false);
      // Clear status after 5 seconds
      setTimeout(() => setUploadStatus(''), 5000);
    }
  }, [updateProperty]);

  const handleBrowseImage = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const renderPropertyField = (key, value) => {
    // Handle different property types
    if (typeof value === 'boolean') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReactSwitch
            checked={value}
            onChange={(checked) => updateProperty(key, checked)}
            onColor="#edbc45"
            offColor="#ccc"
            height={20}
            width={40}
            handleDiameter={16}
            uncheckedIcon={false}
            checkedIcon={false}
          />
          <Typography variant="body2">{key}</Typography>
        </Box>
      );
    }
    
    if (typeof value === 'number') {
      return (
        <TextField
          label={key}
          type="number"
          value={value}
          onChange={(e) => updateProperty(key, parseFloat(e.target.value) || 0)}
          size="small"
          fullWidth
        />
      );
    }
    
    // Color picker for color properties
    if (key.toLowerCase().includes('color') || (typeof value === 'string' && value.startsWith('#'))) {
      return (
        <TextField
          label={key}
          type="color"
          value={value || '#000000'}
          onChange={(e) => updateProperty(key, e.target.value)}
          size="small"
          fullWidth
        />
      );
    }
    
    // Special handling for text alignment
    if (key === 'textAlign') {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>Text Alignment</InputLabel>
          <Select
            value={value || 'left'}
            label="Text Alignment"
            onChange={(e) => updateProperty(key, e.target.value)}
          >
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </Select>
        </FormControl>
      );
    }

    // Special handling for tag selection
    if (key === 'tagId') {
      const selectedTag = availableTags.find(tag => tag.id === value);

      // Debug: Log state but always show Autocomplete
      if (!availableTags || availableTags.length === 0) {
      }

      return (
        <Autocomplete
          size="small"
          fullWidth
          options={availableTags}
          getOptionLabel={(option) => `${option.name} (${option.description || 'No description'})`}
          value={selectedTag || null}
          onChange={(e, newValue) => updateProperty(key, newValue?.id || null)}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: option.type === 'tag' ? '#2196f3' : '#ff9800',
                    marginRight: 1,
                    flexShrink: 0
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: option.type === 'var' ? 'bold' : 'normal' }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description || 'No description'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: option.type === 'tag' ? '#e3f2fd' : '#fff3e0',
                    color: option.type === 'tag' ? '#1565c0' : '#ef6c00',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}
                >
                  {option.type}
                </Box>
              </Box>
            </Box>
          )}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                label="Tag"
                placeholder="Select a tag..."
              />
            );
          }}
          noOptionsText="No tags available"
        />
      );
    }

    // Special handling for custom widget components
    if (key === 'customComponents' && Array.isArray(value)) {
      return (
        <Box>
          <Typography variant="subtitle2" gutterBottom>Components</Typography>
          <Typography variant="body2" color="text.secondary">
            {value.length} component(s): {value.map(comp => comp.type || comp.component).filter(Boolean).join(', ')}
          </Typography>
        </Box>
      );
    }

    // Special handling for custom canvas size
    if (key === 'customCanvasSize' && typeof value === 'object' && value !== null) {
      return (
        <Box>
          <Typography variant="subtitle2" gutterBottom>Canvas Size</Typography>
          <Typography variant="body2" color="text.secondary">
            {value.width} × {value.height}
          </Typography>
        </Box>
      );
    }

    // Special handling for metadata (inputs/outputs)
    if (key === 'metadata' && typeof value === 'object' && value !== null) {
      const inputs = value.inputs || [];
      const outputs = value.outputs || [];
      const parameters = value.parameters || [];

      return (
        <Box>
          <Typography variant="subtitle2" gutterBottom>Widget Interface</Typography>

          {inputs.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="primary">Inputs:</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {inputs.map(input => `${input.name} (${input.type})`).join(', ')}
              </Typography>
            </Box>
          )}

          {outputs.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="success.main">Outputs:</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {outputs.map(output => `${output.name} (${output.type})`).join(', ')}
              </Typography>
            </Box>
          )}

          {parameters.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="warning.main">Parameters:</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {parameters.map(param => `${param.name} (${param.type})`).join(', ')}
              </Typography>
            </Box>
          )}

          {inputs.length === 0 && outputs.length === 0 && parameters.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No interface defined
            </Typography>
          )}
        </Box>
      );
    }

    // Special handling for other objects
    if (typeof value === 'object' && value !== null) {
      return (
        <Box>
          <Typography variant="subtitle2" gutterBottom>{key}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {JSON.stringify(value, null, 2)}
          </Typography>
        </Box>
      );
    }

    // Skip function and undefined values
    if (typeof value === 'function' || value === undefined) {
      return null;
    }

    // Default text field
    return (
      <TextField
        label={key}
        value={value || ''}
        onChange={(e) => updateProperty(key, e.target.value)}
        size="small"
        fullWidth
      />
    );
  };

  if (!widget) {
    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Select a widget to edit properties
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ p: 2, maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}>
      <Grid container spacing={2}>
        {/* Widget Info */}
        <Grid item xs={12}>
          <TextField
            label="Widget ID"
            value={widget.id || ''}
            disabled
            size="small"
            fullWidth
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            label="Widget Type"
            value={widget.type || widget.component || ''}
            disabled
            size="small"
            fullWidth
          />
        </Grid>

        {/* Lock Toggle */}
        {onToggleLock && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={widget.locked ? 'Unlock widget (allow moving)' : 'Lock widget (prevent moving)'}>
                <IconButton
                  onClick={() => onToggleLock(widget.id)}
                  color={widget.locked ? 'warning' : 'default'}
                  sx={{
                    border: '1px solid',
                    borderColor: widget.locked ? 'warning.main' : 'grey.300',
                    borderRadius: 1
                  }}
                >
                  {widget.locked ? <LockIcon /> : <LockOpenIcon />}
                </IconButton>
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
                {widget.locked ? 'Widget is locked' : 'Widget is unlocked'}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Widget Specific Properties */}
        {(widget.type === 'button' || widget.component === 'button') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Button Properties
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Button Text"
                value={properties.text || 'Button'}
                onChange={(e) => updateProperty('text', e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
          </>
        )}

        {/* Custom Widget Interface */}
        {widget && widget.type?.startsWith('custom-') && properties && (() => {
          // Get latest metadata from template (not saved instance)
          let latestMetadata = properties.metadata;
          try {
            // First try from customWidgetDefs prop (always available in designer)
            const widgetIdMatch = widget.type?.match(/custom-(\d+)/);
            const customId = widgetIdMatch ? parseInt(widgetIdMatch[1]) : widget.customWidgetId;
            const defFromProps = customWidgetDefs.find(d => d.customWidgetId === customId || d.customWidgetId === String(customId));
            if (defFromProps && defFromProps.customConfig && defFromProps.customConfig.metadata) {
              latestMetadata = defFromProps.customConfig.metadata;
            } else {
              // Fallback to shared cache
              const templateDef = getCustomWidgetDefinition(widget.type) ||
                (widget.customWidgetId ? getCustomWidgetDefinition(`custom-${widget.customWidgetId}`) : null);
              if (templateDef && templateDef.widgetConfig) {
                const templateConfig = JSON.parse(templateDef.widgetConfig);
                if (templateConfig.metadata) {
                  latestMetadata = templateConfig.metadata;
                }
              }
            }
          } catch (e) { /* use saved metadata as fallback */ }

          if (!latestMetadata) return null;
          const latestInputs = latestMetadata.inputs || [];

          return (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, color: 'primary.main' }}>
                  Custom Widget Interface
                </Typography>
              </Grid>

              {/* Inputs */}
              {latestInputs.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      INPUTS
                    </Typography>
                  </Grid>
                  {latestInputs.map((input, idx) => {
                    const inputBinding = properties[`input_${input.name}`];
                    const isStringType = input.type === 'String' || input.type === 'string' || input.type === 'text';
                    return (
                      <Grid item xs={12} key={`input-${idx}`}>
                        {isStringType ? (
                          <TextField
                            size="small"
                            fullWidth
                            label={`${input.name} (${input.type})`}
                            value={inputBinding || ''}
                            onChange={(e) => updateProperty(`input_${input.name}`, e.target.value)}
                            placeholder="Metin giriniz..."
                          />
                        ) : (
                          <Autocomplete
                            size="small"
                            fullWidth
                            options={availableTags}
                            value={availableTags.find(tag => {
                              if (typeof inputBinding === 'object' && inputBinding !== null) {
                                return tag.id === inputBinding.id;
                              }
                              return tag.id === inputBinding;
                            }) || null}
                            onChange={(e, newValue) => updateProperty(`input_${input.name}`, newValue ? { type: newValue.type, id: newValue.id, showUnit: inputBinding?.showUnit || false } : null)}
                            getOptionLabel={(option) => `${option.name} (${option.type === 'tag' ? 'Tag' : 'Var'})`}
                            renderOption={(props, option) => (
                              <Box component="li" {...props}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: option.type === 'tag' ? '#2196f3' : '#ff9800',
                                      marginRight: 1
                                    }}
                                  />
                                  <Typography variant="body2">
                                    {option.name}
                                  </Typography>
                                  <Box sx={{ ml: 'auto' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {option.type === 'tag' ? 'Tag' : 'Var'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={`${input.name} (${input.type})`}
                                placeholder="Select a tag or variable..."
                              />
                            )}
                          />
                        )}
                        {!isStringType && inputBinding && typeof inputBinding === 'object' && inputBinding.id && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, ml: 0.5 }}>
                            <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>Birim Göster</Typography>
                            <ReactSwitch
                              checked={inputBinding.showUnit || false}
                              onChange={(checked) => {
                                updateProperty(`input_${input.name}`, { ...inputBinding, showUnit: checked });
                              }}
                              height={18}
                              width={36}
                              handleDiameter={14}
                              onColor="#2196f3"
                              offColor="#ccc"
                            />
                          </Box>
                        )}
                      </Grid>
                    );
                  })}
                </>
              )}

              {/* Outputs */}
              {(latestMetadata.outputs || []).length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      OUTPUTS
                    </Typography>
                  </Grid>
                  {latestMetadata.outputs.map((output, idx) => (
                    <Grid item xs={12} key={`output-${idx}`}>
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={availableTags}
                        value={availableTags.find(tag => {
                          const outputValue = properties[`output_${output.name}`];
                          if (typeof outputValue === 'object' && outputValue !== null) {
                            return tag.id === outputValue.id;
                          }
                          return tag.id === outputValue;
                        }) || null}
                        onChange={(e, newValue) => updateProperty(`output_${output.name}`, newValue ? { type: newValue.type, id: newValue.id } : null)}
                        getOptionLabel={(option) => `${option.name} (${option.type === 'tag' ? 'Tag' : 'Var'})`}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: option.type === 'tag' ? '#2196f3' : '#ff9800',
                                  marginRight: 1
                                }}
                              />
                              <Typography variant="body2">
                                {option.name}
                              </Typography>
                              <Box sx={{ ml: 'auto' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {option.type === 'tag' ? 'Tag' : 'Var'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`${output.name} (${output.type})`}
                            placeholder="Select a tag or variable..."
                          />
                        )}
                      />
                    </Grid>
                  ))}
                </>
              )}
            </>
          );
        })()}

        {/* Layout Properties */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            Layout
          </Typography>
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="X Position"
            type="number"
            value={properties.x || 0}
            onChange={(e) => updateProperty('x', parseInt(e.target.value) || 0)}
            size="small"
            fullWidth
          />
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="Y Position"
            type="number"
            value={properties.y || 0}
            onChange={(e) => updateProperty('y', parseInt(e.target.value) || 0)}
            size="small"
            fullWidth
          />
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="Width"
            type="number"
            value={properties.width || 100}
            onChange={(e) => updateProperty('width', parseInt(e.target.value) || 1)}
            size="small"
            fullWidth
          />
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="Height"
            type="number"
            value={properties.height || 50}
            onChange={(e) => updateProperty('height', parseInt(e.target.value) || 1)}
            size="small"
            fullWidth
          />
        </Grid>

        {/* Scale Children Option - for container/custom widgets */}
        {widget && (widget.type?.startsWith('custom-') || widget.components) && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <ReactSwitch
                checked={properties.scaleChildren !== false}
                onChange={(checked) => updateProperty('scaleChildren', checked)}
                onColor="#4caf50"
                offColor="#ccc"
                height={20}
                width={40}
                handleDiameter={16}
                uncheckedIcon={false}
                checkedIcon={false}
              />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Alt elemanlara uygula
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                Boyut değişince içerikler de ölçeklensin
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Style Properties */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            Style Properties
          </Typography>
        </Grid>
        
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Transparent</Typography>
            <ReactSwitch
              checked={properties.backgroundColor === 'transparent' || !properties.backgroundColor}
              onChange={(checked) => {
                if (checked) {
                  updateProperty('backgroundColor', 'transparent');
                } else {
                  updateProperty('backgroundColor', '#ffffff');
                }
              }}
              height={18}
              width={36}
              handleDiameter={14}
              onColor="#2196f3"
              offColor="#ccc"
            />
          </Box>
        </Grid>

        {properties.backgroundColor && properties.backgroundColor !== 'transparent' && (
          <Grid item xs={6}>
            <TextField
              label="Background Color"
              type="color"
              value={properties.backgroundColor || '#ffffff'}
              onChange={(e) => updateProperty('backgroundColor', e.target.value)}
              size="small"
              fullWidth
            />
          </Grid>
        )}
        
        <Grid item xs={6}>
          <TextField
            label="Fore Color"
            type="color"
            value={properties.foreColor || '#000000'}
            onChange={(e) => updateProperty('foreColor', e.target.value)}
            size="small"
            fullWidth
          />
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="Border Width"
            type="number"
            value={properties.borderWidth || 0}
            onChange={(e) => updateProperty('borderWidth', parseInt(e.target.value) || 0)}
            size="small"
            fullWidth
            InputProps={{ inputProps: { min: 0, max: 10 } }}
          />
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="Border Color"
            type="color"
            value={properties.borderColor || '#000000'}
            onChange={(e) => updateProperty('borderColor', e.target.value)}
            size="small"
            fullWidth
          />
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="Border Radius"
            type="number"
            value={properties.borderRadius || 0}
            onChange={(e) => updateProperty('borderRadius', parseInt(e.target.value) || 0)}
            size="small"
            fullWidth
            InputProps={{ inputProps: { min: 0, max: 50 } }}
          />
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="Opacity"
            type="number"
            value={properties.opacity || 1}
            onChange={(e) => updateProperty('opacity', parseFloat(e.target.value) || 1)}
            size="small"
            fullWidth
            InputProps={{ 
              inputProps: { min: 0, max: 1, step: 0.1 }
            }}
          />
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="Font Size"
            type="number"
            value={properties.fontSize || 14}
            onChange={(e) => updateProperty('fontSize', parseInt(e.target.value) || 14)}
            size="small"
            fullWidth
            InputProps={{ inputProps: { min: 8, max: 72 } }}
          />
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="Font Weight"
            type="number"
            value={properties.fontWeight || 400}
            onChange={(e) => updateProperty('fontWeight', parseInt(e.target.value) || 400)}
            size="small"
            fullWidth
            InputProps={{ 
              inputProps: { min: 100, max: 900, step: 100 }
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            label="Font Family"
            value={properties.fontFamily || 'inherit'}
            onChange={(e) => updateProperty('fontFamily', e.target.value)}
            size="small"
            fullWidth
          />
        </Grid>

        {/* Data Binding */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            Data Binding
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <Autocomplete
            size="small"
            fullWidth
            options={availableTags}
            getOptionLabel={(option) => `${option.name} (${option.type === 'tag' ? 'Tag' : 'Var'})`}
            value={availableTags.find(tag => {
              const tagIdValue = properties.tagId;
              if (typeof tagIdValue === 'object' && tagIdValue !== null) {
                return tag.id === tagIdValue.id;
              }
              return tag.id === tagIdValue;
            }) || null}
            onChange={(e, newValue) => updateProperty('tagId', newValue ? { type: newValue.type, id: newValue.id } : null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tag"
                placeholder="Select a tag..."
              />
            )}
          />
        </Grid>

        {/* Text Properties for TextLabel */}
        {(widget?.type === 'text-label' || widget?.component === 'TextLabel' || widget?.type === 'TextLabel') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Text Properties
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Text Content"
                value={properties.text || 'Label'}
                onChange={(e) => updateProperty('text', e.target.value)}
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Enter text content"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Text Alignment</InputLabel>
                <Select
                  value={properties.textAlign || 'left'}
                  onChange={(e) => updateProperty('textAlign', e.target.value)}
                  label="Text Alignment"
                >
                  <MenuItem value="left">Left</MenuItem>
                  <MenuItem value="center">Center</MenuItem>
                  <MenuItem value="right">Right</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Font Size"
                value={properties.fontSize || '14px'}
                onChange={(e) => updateProperty('fontSize', e.target.value)}
                fullWidth
                size="small"
                placeholder="14px"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Font Weight</InputLabel>
                <Select
                  value={properties.fontWeight || 'normal'}
                  onChange={(e) => updateProperty('fontWeight', e.target.value)}
                  label="Font Weight"
                >
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="bold">Bold</MenuItem>
                  <MenuItem value="lighter">Light</MenuItem>
                  <MenuItem value="bolder">Bolder</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Font Family"
                value={properties.fontFamily || 'inherit'}
                onChange={(e) => updateProperty('fontFamily', e.target.value)}
                fullWidth
                size="small"
                placeholder="Arial, sans-serif"
              />
            </Grid>

            {/* Unit and Number Format for tag values */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Value Formatting
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Unit"
                value={properties.unit || ''}
                onChange={(e) => updateProperty('unit', e.target.value)}
                fullWidth
                size="small"
                placeholder="°C, %, kW/h"
              />
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', mb: 0.3 }}>Birim</Typography>
                <ReactSwitch
                  checked={properties.showUnit !== false}
                  onChange={(checked) => updateProperty('showUnit', checked)}
                  height={18}
                  width={36}
                  handleDiameter={14}
                  onColor="#2196f3"
                  offColor="#ccc"
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Decimals"
                type="number"
                value={properties.numberFormat?.decimals ?? 2}
                onChange={(e) => updateProperty('numberFormat', {
                  ...properties.numberFormat,
                  decimals: parseInt(e.target.value) || 0,
                  decimalSeparator: properties.numberFormat?.decimalSeparator || ',',
                  thousandsSeparator: properties.numberFormat?.thousandsSeparator || '.'
                })}
                fullWidth
                size="small"
                InputProps={{ inputProps: { min: 0, max: 6 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Decimal Sep.</InputLabel>
                <Select
                  value={properties.numberFormat?.decimalSeparator || ','}
                  onChange={(e) => updateProperty('numberFormat', {
                    ...properties.numberFormat,
                    decimals: properties.numberFormat?.decimals ?? 2,
                    decimalSeparator: e.target.value,
                    thousandsSeparator: properties.numberFormat?.thousandsSeparator || '.'
                  })}
                  label="Decimal Sep."
                >
                  <MenuItem value=",">, (Virgül)</MenuItem>
                  <MenuItem value=".">. (Nokta)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Thousands Sep.</InputLabel>
                <Select
                  value={properties.numberFormat?.thousandsSeparator || '.'}
                  onChange={(e) => updateProperty('numberFormat', {
                    ...properties.numberFormat,
                    decimals: properties.numberFormat?.decimals ?? 2,
                    decimalSeparator: properties.numberFormat?.decimalSeparator || ',',
                    thousandsSeparator: e.target.value
                  })}
                  label="Thousands Sep."
                >
                  <MenuItem value=".">. (Nokta)</MenuItem>
                  <MenuItem value=",">, (Virgül)</MenuItem>
                  <MenuItem value=" ">  (Boşluk)</MenuItem>
                  <MenuItem value="">Yok</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Value Mapping (Tag değerine göre text gösterme) */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Value Mapping
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Tag değerine göre gösterilecek metni belirleyin (örn: 0→Normal, 1→Alarm)
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {Object.entries(properties.valueMap || {}).map(([key, text], idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 0.5, mb: 1, alignItems: 'center' }}>
                  <TextField
                    label="Değer"
                    value={key}
                    onChange={(e) => {
                      const newMap = { ...(properties.valueMap || {}) };
                      const oldText = newMap[key];
                      delete newMap[key];
                      newMap[e.target.value] = oldText;
                      // colorMap key'ini de güncelle
                      if (properties.colorMap) {
                        const newCM = { ...(properties.colorMap || {}) };
                        const oldColor = newCM[key];
                        delete newCM[key];
                        if (oldColor) newCM[e.target.value] = oldColor;
                        updateProperty('colorMap', Object.keys(newCM).length > 0 ? newCM : undefined);
                      }
                      updateProperty('valueMap', newMap);
                    }}
                    size="small"
                    sx={{ width: '70px' }}
                  />
                  <TextField
                    label="Text"
                    value={text}
                    onChange={(e) => {
                      const newMap = { ...(properties.valueMap || {}) };
                      newMap[key] = e.target.value;
                      updateProperty('valueMap', newMap);
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <input
                    type="color"
                    value={(properties.colorMap || {})[key] || '#ffffff'}
                    title="Arkaplan rengi"
                    onChange={(e) => {
                      const newCM = { ...(properties.colorMap || {}) };
                      newCM[key] = e.target.value === '#ffffff' ? undefined : e.target.value;
                      // Temizle: undefined olanları sil
                      Object.keys(newCM).forEach(k => { if (!newCM[k]) delete newCM[k]; });
                      updateProperty('colorMap', Object.keys(newCM).length > 0 ? newCM : undefined);
                    }}
                    style={{ width: 28, height: 28, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      const newMap = { ...(properties.valueMap || {}) };
                      delete newMap[key];
                      updateProperty('valueMap', Object.keys(newMap).length > 0 ? newMap : undefined);
                      // colorMap'ten de sil
                      if (properties.colorMap) {
                        const newCM = { ...(properties.colorMap || {}) };
                        delete newCM[key];
                        updateProperty('colorMap', Object.keys(newCM).length > 0 ? newCM : undefined);
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  const newMap = { ...(properties.valueMap || {}) };
                  let newKey = '0';
                  let counter = 0;
                  while (newMap.hasOwnProperty(newKey)) {
                    counter++;
                    newKey = String(counter);
                  }
                  newMap[newKey] = '';
                  updateProperty('valueMap', newMap);
                }}
                variant="outlined"
                sx={{ mt: 0.5 }}
              >
                Mapping Ekle
              </Button>
            </Grid>
          </>
        )}

        {/* Button Specific Properties */}
        {(widget?.type === 'button' || widget?.component === 'Button') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Button Properties
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Button Text"
                value={properties.text || 'Button'}
                onChange={(e) => updateProperty('text', e.target.value)}
                fullWidth
                size="small"
                placeholder="Enter button text"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Variant</InputLabel>
                <Select
                  value={properties.variant || 'contained'}
                  onChange={(e) => updateProperty('variant', e.target.value)}
                  label="Variant"
                >
                  <MenuItem value="contained">Contained</MenuItem>
                  <MenuItem value="outlined">Outlined</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.disabled || false}
                  onChange={(checked) => updateProperty('disabled', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Disabled</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Arkaplan Rengi"
                type="color"
                value={properties.backgroundColor || '#1976d2'}
                onChange={(e) => updateProperty('backgroundColor', e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Yazı Rengi"
                type="color"
                value={properties.foreColor || '#ffffff'}
                onChange={(e) => updateProperty('foreColor', e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
          </>
        )}

        {/* TextBox Specific Properties */}}
        {(widget?.type === 'text-box' || widget?.component === 'TextBox') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                TextBox Properties
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Default Value"
                value={properties.value || ''}
                onChange={(e) => updateProperty('value', e.target.value)}
                fullWidth
                size="small"
                placeholder="Enter default value"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Placeholder"
                value={properties.placeholder || 'Enter text...'}
                onChange={(e) => updateProperty('placeholder', e.target.value)}
                fullWidth
                size="small"
                placeholder="Enter placeholder text"
              />
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.multiline || false}
                  onChange={(checked) => updateProperty('multiline', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Multiline</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.readOnly || false}
                  onChange={(checked) => updateProperty('readOnly', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  disabled={properties.tagId ? true : false}
                />
                <Typography variant="body2">Read Only</Typography>
              </Box>
            </Grid>
            {properties.multiline && (
              <Grid item xs={6}>
                <TextField
                  label="Rows"
                  type="number"
                  value={properties.rows || 3}
                  onChange={(e) => updateProperty('rows', parseInt(e.target.value))}
                  fullWidth
                  size="small"
                  InputProps={{ inputProps: { min: 1, max: 10 } }}
                />
              </Grid>
            )}
          </>
        )}

        {/* ToggleSwitch Specific Properties */}
        {(widget?.type === 'toggle-switch' || widget?.component === 'ToggleSwitch') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Toggle Switch Properties
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Switch Label"
                value={properties.label || ''}
                onChange={(e) => updateProperty('label', e.target.value)}
                fullWidth
                size="small"
                placeholder="Switch label (optional)"
              />
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.checked || false}
                  onChange={(checked) => updateProperty('checked', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  disabled={properties.tagId ? true : false}
                />
                <Typography variant="body2">Default State</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.disabled || false}
                  onChange={(checked) => updateProperty('disabled', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Disabled</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Size</InputLabel>
                <Select
                  value={properties.size || 'medium'}
                  onChange={(e) => updateProperty('size', e.target.value)}
                  label="Size"
                >
                  <MenuItem value="small">Small</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="large">Large</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Color"
                value={properties.color || '#1976d2'}
                onChange={(e) => updateProperty('color', e.target.value)}
                fullWidth
                size="small"
                type="color"
              />
            </Grid>
          </>
        )}

        {/* SetValue Specific Properties */}
        {(widget?.type === 'set-value' || widget?.component === 'SetValue') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Set Value Properties
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Text Alignment</InputLabel>
                <Select
                  value={properties.textAlign || 'center'}
                  onChange={(e) => updateProperty('textAlign', e.target.value)}
                  label="Text Alignment"
                >
                  <MenuItem value="left">Left</MenuItem>
                  <MenuItem value="center">Center</MenuItem>
                  <MenuItem value="right">Right</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Unit"
                value={properties.unit || ''}
                onChange={(e) => updateProperty('unit', e.target.value)}
                fullWidth
                size="small"
                placeholder="°C, %, kW/h"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Decimals"
                type="number"
                value={properties.numberFormat?.decimals ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || val === null) {
                    updateProperty('numberFormat', null);
                  } else {
                    updateProperty('numberFormat', {
                      ...properties.numberFormat,
                      decimals: parseInt(val) || 0,
                      decimalSeparator: properties.numberFormat?.decimalSeparator || ',',
                      thousandsSeparator: properties.numberFormat?.thousandsSeparator || '.'
                    });
                  }
                }}
                fullWidth
                size="small"
                InputProps={{ inputProps: { min: 0, max: 6 } }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Dec. Sep.</InputLabel>
                <Select
                  value={properties.numberFormat?.decimalSeparator || ','}
                  onChange={(e) => updateProperty('numberFormat', {
                    ...properties.numberFormat,
                    decimals: properties.numberFormat?.decimals ?? 2,
                    decimalSeparator: e.target.value,
                    thousandsSeparator: properties.numberFormat?.thousandsSeparator || '.'
                  })}
                  label="Dec. Sep."
                >
                  <MenuItem value=",">, (Virgül)</MenuItem>
                  <MenuItem value=".">. (Nokta)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>1000 Sep.</InputLabel>
                <Select
                  value={properties.numberFormat?.thousandsSeparator || '.'}
                  onChange={(e) => updateProperty('numberFormat', {
                    ...properties.numberFormat,
                    decimals: properties.numberFormat?.decimals ?? 2,
                    decimalSeparator: properties.numberFormat?.decimalSeparator || ',',
                    thousandsSeparator: e.target.value
                  })}
                  label="1000 Sep."
                >
                  <MenuItem value=".">. (Nokta)</MenuItem>
                  <MenuItem value=",">, (Virgül)</MenuItem>
                  <MenuItem value=" ">  (Boşluk)</MenuItem>
                  <MenuItem value="">Yok</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {/* ProgressBar Specific Properties */}
        {(widget?.type === 'progress-bar' || widget?.component === 'ProgressBar') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Progress Bar Properties
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Value"
                type="number"
                value={properties.value || 50}
                onChange={(e) => updateProperty('value', parseFloat(e.target.value))}
                fullWidth
                size="small"
                InputProps={{ inputProps: { min: properties.min || 0, max: properties.max || 100 } }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Min Value"
                type="number"
                value={properties.min || 0}
                onChange={(e) => updateProperty('min', parseFloat(e.target.value))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Max Value"
                type="number"
                value={properties.max || 100}
                onChange={(e) => updateProperty('max', parseFloat(e.target.value))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.showValue !== undefined ? properties.showValue : true}
                  onChange={(checked) => updateProperty('showValue', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Show Value</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.vertical || false}
                  onChange={(checked) => updateProperty('vertical', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Vertical</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Thickness"
                type="number"
                value={properties.thickness || 4}
                onChange={(e) => updateProperty('thickness', parseInt(e.target.value))}
                fullWidth
                size="small"
                InputProps={{ inputProps: { min: 2, max: 20 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Color"
                value={properties.color || '#1976d2'}
                onChange={(e) => updateProperty('color', e.target.value)}
                fullWidth
                size="small"
                type="color"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Background Color"
                value={properties.backgroundColor || '#e0e0e0'}
                onChange={(e) => updateProperty('backgroundColor', e.target.value)}
                fullWidth
                size="small"
                type="color"
              />
            </Grid>
          </>
        )}

        {/* Gauge Specific Properties */}
        {(widget?.type === 'gauge' || widget?.component === 'Gauge') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Gauge Properties
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Value"
                type="number"
                value={properties.value || 0}
                onChange={(e) => updateProperty('value', parseFloat(e.target.value))}
                fullWidth
                size="small"
                InputProps={{ inputProps: { min: properties.min || 0, max: properties.max || 100 } }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Min Value"
                type="number"
                value={properties.min || 0}
                onChange={(e) => updateProperty('min', parseFloat(e.target.value))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Max Value"
                type="number"
                value={properties.max || 100}
                onChange={(e) => updateProperty('max', parseFloat(e.target.value))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Start Angle"
                type="number"
                value={properties.startAngle !== undefined ? properties.startAngle : -90}
                onChange={(e) => updateProperty('startAngle', parseInt(e.target.value))}
                fullWidth
                size="small"
                InputProps={{ inputProps: { min: -180, max: 180 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Angle"
                type="number"
                value={properties.endAngle !== undefined ? properties.endAngle : 90}
                onChange={(e) => updateProperty('endAngle', parseInt(e.target.value))}
                fullWidth
                size="small"
                InputProps={{ inputProps: { min: -180, max: 180 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Unit"
                value={properties.unit || ''}
                onChange={(e) => updateProperty('unit', e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g., %, °C, RPM"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Title"
                value={properties.title || ''}
                onChange={(e) => updateProperty('title', e.target.value)}
                fullWidth
                size="small"
                placeholder="Gauge title (optional)"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Zone Settings
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.showZones !== undefined ? properties.showZones : true}
                  onChange={(checked) => updateProperty('showZones', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Show Colored Zones</Typography>
              </Box>
            </Grid>
            
            {properties.showZones !== false && (
              <>
                <Grid item xs={6}>
                  <TextField
                    label="Normal Zone End"
                    type="number"
                    value={properties.normalZoneEnd || 60}
                    onChange={(e) => updateProperty('normalZoneEnd', parseFloat(e.target.value))}
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: properties.min || 0, max: properties.max || 100 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Warning Zone End"
                    type="number"
                    value={properties.warningZoneEnd || 80}
                    onChange={(e) => updateProperty('warningZoneEnd', parseFloat(e.target.value))}
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: properties.min || 0, max: properties.max || 100 } }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Normal Color"
                    value={properties.normalColor || '#4caf50'}
                    onChange={(e) => updateProperty('normalColor', e.target.value)}
                    fullWidth
                    size="small"
                    type="color"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Warning Color"
                    value={properties.warningColor || '#ff9800'}
                    onChange={(e) => updateProperty('warningColor', e.target.value)}
                    fullWidth
                    size="small"
                    type="color"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Danger Color"
                    value={properties.dangerColor || '#f44336'}
                    onChange={(e) => updateProperty('dangerColor', e.target.value)}
                    fullWidth
                    size="small"
                    type="color"
                  />
                </Grid>
              </>
            )}
          </>
        )}

        {/* LED Indicator Specific Properties */}
        {(widget?.type === 'led-indicator' || widget?.component === 'LEDIndicator') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                LED Properties
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.state === 'active' || properties.state === true || false}
                  onChange={(checked) => updateProperty('state', checked ? 'active' : 'inactive')}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  disabled={properties.tagId ? true : false}
                />
                <Typography variant="body2">LED State</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.blink || false}
                  onChange={(checked) => updateProperty('blink', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Blink Effect</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="LED Size"
                type="number"
                value={properties.size || 24}
                onChange={(e) => updateProperty('size', parseInt(e.target.value))}
                fullWidth
                size="small"
                InputProps={{ inputProps: { min: 10, max: 100 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Active Color"
                value={properties.color || '#4caf50'}
                onChange={(e) => updateProperty('color', e.target.value)}
                fullWidth
                size="small"
                type="color"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Inactive Color"
                value={properties.offColor || '#cccccc'}
                onChange={(e) => updateProperty('offColor', e.target.value)}
                fullWidth
                size="small"
                type="color"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Label"
                value={properties.label || ''}
                onChange={(e) => updateProperty('label', e.target.value)}
                fullWidth
                size="small"
                placeholder="LED label (optional)"
              />
            </Grid>
          </>
        )}

        {/* Gauge Specific Properties */}
        {(widget?.type === 'Gauge' || widget?.component === 'Gauge') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Gauge Properties
              </Typography>
            </Grid>
            
            {/* Range Settings */}
            <Grid item xs={6}>
              <TextField
                label="Min Value"
                type="number"
                value={properties.min !== undefined ? properties.min : 0}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  updateProperty('min', isNaN(value) ? 0 : value);
                }}
                size="small"
                fullWidth
                helperText="Minimum gauge value"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Max Value"
                type="number"
                value={properties.max !== undefined ? properties.max : 100}
                onChange={(e) => {
                  const value = e.target.value === '' ? 100 : parseFloat(e.target.value);
                  updateProperty('max', isNaN(value) ? 100 : value);
                }}
                size="small"
                fullWidth
                helperText="Maximum gauge value"
              />
            </Grid>
            
            {/* Angle Settings */}
            <Grid item xs={6}>
              <TextField
                label="Start Angle"
                type="number"
                value={properties.startAngle !== undefined ? properties.startAngle : -90}
                onChange={(e) => {
                  const value = e.target.value === '' ? -90 : parseInt(e.target.value);
                  updateProperty('startAngle', isNaN(value) ? -90 : value);
                }}
                size="small"
                fullWidth
                InputProps={{ 
                  inputProps: { min: -360, max: 360 }
                }}
                helperText="0=right, -90=top, -180=left"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Angle"
                type="number"
                value={properties.endAngle !== undefined ? properties.endAngle : 270}
                onChange={(e) => {
                  const value = e.target.value === '' ? 270 : parseInt(e.target.value);
                  updateProperty('endAngle', isNaN(value) ? 270 : value);
                }}
                size="small"
                fullWidth
                InputProps={{ 
                  inputProps: { min: -360, max: 720 }
                }}
                helperText="270=3/4 circle, 0=right"
              />
            </Grid>
            
            {/* Zone Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Zone Settings
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.showZones !== undefined ? properties.showZones : true}
                  onChange={(checked) => updateProperty('showZones', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Show Colored Zones</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Normal Zone End (%)"
                type="number"
                value={properties.normalZoneEnd !== undefined ? properties.normalZoneEnd : 70}
                onChange={(e) => {
                  const value = e.target.value === '' ? 70 : parseFloat(e.target.value);
                  updateProperty('normalZoneEnd', isNaN(value) ? 70 : Math.max(0, Math.min(100, value)));
                }}
                size="small"
                fullWidth
                InputProps={{ 
                  inputProps: { min: 0, max: 100 }
                }}
                helperText="Green zone: 0% to this value"
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Warning Zone End (%)"
                type="number"
                value={properties.warningZoneEnd !== undefined ? properties.warningZoneEnd : 90}
                onChange={(e) => {
                  const value = e.target.value === '' ? 90 : parseFloat(e.target.value);
                  updateProperty('warningZoneEnd', isNaN(value) ? 90 : Math.max(0, Math.min(100, value)));
                }}
                size="small"
                fullWidth
                InputProps={{ 
                  inputProps: { min: 0, max: 100 }
                }}
                helperText="Yellow zone: normal end to this value"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Red (alarm) zone: warning zone end to 100%
              </Typography>
            </Grid>
            
            {/* Unit Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Display Settings
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Unit"
                value={properties.unit || ''}
                onChange={(e) => updateProperty('unit', e.target.value)}
                size="small"
                fullWidth
                placeholder="°C, %, bar, etc."
                helperText="Unit symbol displayed with value"
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Title"
                value={properties.title || ''}
                onChange={(e) => updateProperty('title', e.target.value)}
                size="small"
                fullWidth
                placeholder="Gauge title"
                helperText="Title shown above gauge"
              />
            </Grid>
          </>
        )}

        {/* Button Properties */}
        {(widget?.type === 'Button' || widget?.component === 'Button') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Button Properties
              </Typography>
            </Grid>
            
            <Grid item xs={8}>
              <TextField
                label="Button Text"
                value={properties.text || 'Button'}
                onChange={(e) => updateProperty('text', e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Variant</InputLabel>
                <Select
                  value={properties.variant || 'contained'}
                  label="Variant"
                  onChange={(e) => updateProperty('variant', e.target.value)}
                >
                  <MenuItem value="contained">Contained</MenuItem>
                  <MenuItem value="outlined">Outlined</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.disabled || false}
                  onChange={(checked) => updateProperty('disabled', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Disabled</Typography>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Arkaplan Rengi"
                type="color"
                value={properties.backgroundColor || '#1976d2'}
                onChange={(e) => updateProperty('backgroundColor', e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Yazı Rengi"
                type="color"
                value={properties.foreColor || '#ffffff'}
                onChange={(e) => updateProperty('foreColor', e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Button Actions
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={properties.actionType || 'none'}
                  label="Action Type"
                  onChange={(e) => updateProperty('actionType', e.target.value)}
                >
                  <MenuItem value="none">No Action</MenuItem>
                  <MenuItem value="tag-operation">Tag Operation</MenuItem>
                  <MenuItem value="navigation">Navigation</MenuItem>
                  <MenuItem value="start-task">Start Task</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Tag Operation Configuration */}
            {properties.actionType === 'tag-operation' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                    Tag Operation Settings
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={availableTags}
                    getOptionLabel={(option) => `${option.name} (${option.type === 'tag' ? 'Tag' : 'Var'})`}
                    value={availableTags.find(tag => {
                      const targetTagIdValue = properties.targetTagId;
                      if (typeof targetTagIdValue === 'object' && targetTagIdValue !== null) {
                        return tag.id === targetTagIdValue.id;
                      }
                      return tag.id === targetTagIdValue;
                    }) || null}
                    onChange={(e, newValue) => updateProperty('targetTagId', newValue ? { type: newValue.type, id: newValue.id } : null)}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: option.type === 'tag' ? '#2196f3' : '#ff9800',
                              marginRight: 1,
                              flexShrink: 0
                            }}
                          />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: option.type === 'var' ? 'bold' : 'normal' }}>
                              {option.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.description || 'No description'}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              backgroundColor: option.type === 'tag' ? '#e3f2fd' : '#fff3e0',
                              color: option.type === 'tag' ? '#1565c0' : '#ef6c00',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              textTransform: 'uppercase'
                            }}
                          >
                            {option.type}
                          </Box>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Target Tag"
                        placeholder="Select tag to modify..."
                      />
                    )}
                    noOptionsText="No tags available"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Operation</InputLabel>
                    <Select
                      value={properties.tagOperation || 'set'}
                      label="Operation"
                      onChange={(e) => updateProperty('tagOperation', e.target.value)}
                    >
                      <MenuItem value="set">Set Value</MenuItem>
                      <MenuItem value="toggle">Toggle (Boolean)</MenuItem>
                      <MenuItem value="increment">Increment</MenuItem>
                      <MenuItem value="decrement">Decrement</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {properties.tagOperation === 'set' && (
                  <Grid item xs={6}>
                    <TextField
                      label="Set Value"
                      value={properties.tagSetValue || ''}
                      onChange={(e) => updateProperty('tagSetValue', e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="Value to set"
                    />
                  </Grid>
                )}
                
                {(properties.tagOperation === 'increment' || properties.tagOperation === 'decrement') && (
                  <Grid item xs={6}>
                    <TextField
                      label="Step Value"
                      type="number"
                      value={properties.tagIncrementValue || 1}
                      onChange={(e) => updateProperty('tagIncrementValue', parseFloat(e.target.value) || 1)}
                      size="small"
                      fullWidth
                      placeholder="1"
                    />
                  </Grid>
                )}
              </>
            )}
            
            {/* Navigation Configuration */}
            {properties.actionType === 'navigation' && (() => {
              // Normalize navigateTo to /scada/view/{id} format for dropdown matching
              const rawNav = properties.navigateTo || '';
              let normalizedNav = rawNav;
              if (rawNav && !rawNav.includes('/scada/view/')) {
                let pageId = null;
                if (!isNaN(rawNav)) {
                  pageId = rawNav;
                } else if (rawNav.includes('id=')) {
                  try { pageId = new URLSearchParams(rawNav.split('?')[1]).get('id'); } catch(e) {}
                }
                if (pageId) normalizedNav = `/scada/view/${pageId}`;
              }
              return (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                    Navigation Settings
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Navigate To Page</InputLabel>
                    <Select
                      value={normalizedNav}
                      label="Navigate To Page"
                      onChange={(e) => updateProperty('navigateTo', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select a page...</em>
                      </MenuItem>
                      {availablePages.map((page) => (
                        <MenuItem key={page.id} value={page.path}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2">{page.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {page.path}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Navigate to another SCADA page in this system
                  </Typography>
                </Grid>
              </>
              );
            })()}
            
            {/* Start Task Configuration */}
            {properties.actionType === 'start-task' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                    Task Settings
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Task Command"
                    value={properties.taskCommand || ''}
                    onChange={(e) => updateProperty('taskCommand', e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="start-motor, backup-data, etc."
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Parameters (Optional)"
                    value={properties.taskParameters || ''}
                    onChange={(e) => updateProperty('taskParameters', e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="JSON parameters or command arguments"
                  />
                </Grid>
              </>
            )}
          </>
        )}

        {/* ProgressBar Properties */}
        {(widget?.type === 'ProgressBar' || widget?.component === 'ProgressBar') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                ProgressBar Properties
              </Typography>
            </Grid>
            
            {/* Range Settings */}
            <Grid item xs={4}>
              <TextField
                label="Min Value"
                type="number"
                value={properties.min !== undefined ? properties.min : 0}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  updateProperty('min', isNaN(value) ? 0 : value);
                }}
                size="small"
                fullWidth
              />
            </Grid>
            
            <Grid item xs={4}>
              <TextField
                label="Max Value"
                type="number"
                value={properties.max !== undefined ? properties.max : 100}
                onChange={(e) => {
                  const value = e.target.value === '' ? 100 : parseFloat(e.target.value);
                  updateProperty('max', isNaN(value) ? 100 : value);
                }}
                size="small"
                fullWidth
              />
            </Grid>
            
            <Grid item xs={4}>
              <TextField
                label="Current Value"
                type="number"
                value={properties.value !== undefined ? properties.value : 50}
                onChange={(e) => {
                  const value = e.target.value === '' ? 50 : parseFloat(e.target.value);
                  updateProperty('value', isNaN(value) ? 50 : value);
                }}
                size="small"
                fullWidth
                disabled={properties.tagId ? true : false}
                helperText={properties.tagId ? "Controlled by tag" : ""}
              />
            </Grid>
            
            {/* Display Settings */}
            <Grid item xs={6}>
              <TextField
                label="Unit"
                value={properties.unit || '%'}
                onChange={(e) => updateProperty('unit', e.target.value)}
                size="small"
                fullWidth
                placeholder="%"
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label={properties.circular ? "Thickness" : "Bar Height (px)"}
                type="number"
                value={properties.circular ? (properties.thickness || 4) : (properties.height || 20)}
                onChange={(e) => updateProperty(properties.circular ? 'thickness' : 'height', parseInt(e.target.value) || (properties.circular ? 4 : 20))}
                size="small"
                fullWidth
                InputProps={{ inputProps: { min: properties.circular ? 1 : 5, max: properties.circular ? 20 : 100 } }}
              />
            </Grid>
            
            <Grid item xs={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.showValue !== undefined ? properties.showValue : true}
                  onChange={(checked) => updateProperty('showValue', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Show Value</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.vertical || false}
                  onChange={(checked) => updateProperty('vertical', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  disabled={properties.circular}
                />
                <Typography variant="body2" sx={{ opacity: properties.circular ? 0.5 : 1 }}>Vertical</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.circular || false}
                  onChange={(checked) => updateProperty('circular', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Circular</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Color</InputLabel>
                <Select
                  value={properties.color || 'primary'}
                  label="Color"
                  onChange={(e) => updateProperty('color', e.target.value)}
                >
                  <MenuItem value="primary">Primary</MenuItem>
                  <MenuItem value="secondary">Secondary</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="info">Info</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {/* Image Properties */}
        {(widget?.type === 'Image' || widget?.component === 'Image') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Image Properties
              </Typography>
            </Grid>
            
            <Grid item xs={8}>
              <TextField
                label="İmaj Kaynağı (src)"
                value={properties.src || ''}
                onChange={(e) => updateProperty('src', e.target.value)}
                size="small"
                fullWidth
                placeholder="Dosya adı veya URL"
                helperText="Örn: image.png veya http://example.com/image.jpg"
              />
            </Grid>
            
            <Grid item xs={4}>
              <Button
                variant="contained"
                onClick={handleBrowseImage}
                disabled={uploading}
                fullWidth
                sx={{ height: 40 }}
              >
                {uploading ? 'Yükleniyor...' : 'Dosya Seç'}
              </Button>
            </Grid>
            
            {uploadStatus && (
              <Grid item xs={12}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: uploadStatus.includes('✅') ? 'green' : 'red',
                    fontWeight: 'bold'
                  }}
                >
                  {uploadStatus}
                </Typography>
              </Grid>
            )}
            
            <Grid item xs={6}>
              <TextField
                label="Alt Metni"
                value={properties.alt || ''}
                onChange={(e) => updateProperty('alt', e.target.value)}
                size="small"
                fullWidth
                placeholder="Resim açıklaması"
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Sığdırma Modu"
                value={properties.fit || 'contain'}
                onChange={(e) => updateProperty('fit', e.target.value)}
                size="small"
                fullWidth
                placeholder="contain, cover, fill, none, scale-down"
                helperText="CSS object-fit değeri"
              />
            </Grid>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </>
        )}

        {/* ToggleSwitch Properties */}
        {(widget?.type === 'ToggleSwitch' || widget?.component === 'ToggleSwitch') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Toggle Switch Properties
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Label"
                value={properties.label || ''}
                onChange={(e) => updateProperty('label', e.target.value)}
                size="small"
                fullWidth
                placeholder="Switch label (optional)"
              />
            </Grid>
            
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.checked || false}
                  onChange={(checked) => updateProperty('checked', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  disabled={properties.tagId ? true : false}
                />
                <Typography variant="body2">Default State</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.disabled || false}
                  onChange={(checked) => updateProperty('disabled', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Disabled</Typography>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Size</InputLabel>
                <Select
                  value={properties.size || 'medium'}
                  label="Size"
                  onChange={(e) => updateProperty('size', e.target.value)}
                >
                  <MenuItem value="small">Small</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Color</InputLabel>
                <Select
                  value={properties.color || 'primary'}
                  label="Color"
                  onChange={(e) => updateProperty('color', e.target.value)}
                >
                  <MenuItem value="primary">Primary</MenuItem>
                  <MenuItem value="secondary">Secondary</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="info">Info</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {/* TrendChart Properties */}
        {(widget?.type === 'TrendChart' || widget?.component === 'TrendChart') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Trend Chart Properties
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Title"
                value={properties.title || ''}
                onChange={(e) => updateProperty('title', e.target.value)}
                size="small"
                fullWidth
                placeholder="Chart title (optional)"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                multiple
                size="small"
                fullWidth
                options={availableTags}
                getOptionLabel={(option) => `${option.name} (${option.description || 'No description'})`}
                value={(() => {
                  // Priority: local temp state > widget properties > local properties
                  let currentTagIds;
                  
                  if (trendTagIdsLocal !== null) {
                    currentTagIds = trendTagIdsLocal;
                  } else {
                    const widgetTagIds = widget?.properties?.tagIds || widget?.props?.tagIds || [];
                    const localTagIds = properties.tagIds || [];
                    currentTagIds = Array.isArray(widgetTagIds) ? widgetTagIds : localTagIds;
                  }
                  
                  return availableTags.filter(tag => {
                    const isSelected = currentTagIds.includes(tag.id);
                    if (isSelected) {
                    }
                    return isSelected;
                  });
                })()}
                onChange={(e, newValues) => {
                  const tagIds = newValues.map(tag => tag.id).slice(0, 5); // Limit to 5 tags
                  
                  // Set local temp state immediately for UI responsiveness
                  setTrendTagIdsLocal(tagIds);
                  
                  updateProperty('tagIds', tagIds);
                  // Note: Removed trendTagId update as it was causing conflicts
                }}
                limitTags={5}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: option.type === 'tag' ? '#2196f3' : '#ff9800',
                          marginRight: 1,
                          flexShrink: 0
                        }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: option.type === 'var' ? 'bold' : 'normal' }}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description || 'No description'}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: option.type === 'tag' ? '#e3f2fd' : '#fff3e0',
                          color: option.type === 'tag' ? '#1565c0' : '#ef6c00',
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          textTransform: 'uppercase'
                        }}
                      >
                        {option.type}
                      </Box>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Trend Data Sources (Max 5)"
                    placeholder="Select tags/variables to trend..."
                    helperText={`Selected ${(() => {
                      const widgetTagIds = widget?.properties?.tagIds || widget?.props?.tagIds || [];
                      const localTagIds = properties.tagIds || [];
                      const currentTagIds = widgetTagIds.length > 0 ? widgetTagIds : localTagIds;
                      return currentTagIds.length;
                    })()}/5 tags. Multiple trends will be overlaid with different colors.`}
                  />
                )}
                noOptionsText="No tags available"
              />
            </Grid>
            
            {(() => {
              const widgetTagIds = widget?.properties?.tagIds || widget?.props?.tagIds || [];
              const localTagIds = properties.tagIds || [];
              const currentTagIds = widgetTagIds.length > 0 ? widgetTagIds : localTagIds;
              return currentTagIds && currentTagIds.length > 0;
            })() && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
                  📊 Selected Trends: {(() => {
                    const widgetTagIds = widget?.properties?.tagIds || widget?.props?.tagIds || [];
                    const localTagIds = properties.tagIds || [];
                    const currentTagIds = widgetTagIds.length > 0 ? widgetTagIds : localTagIds;
                    return currentTagIds.length;
                  })()} tag(s)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(() => {
                    const widgetTagIds = widget?.properties?.tagIds || widget?.props?.tagIds || [];
                    const localTagIds = properties.tagIds || [];
                    const currentTagIds = widgetTagIds.length > 0 ? widgetTagIds : localTagIds;
                    return currentTagIds;
                  })().map((tagId, index) => {
                    const tag = availableTags.find(t => t.id === tagId);
                    const colors = ['#1976d2', '#dc004e', '#2e7d32', '#ed6c02', '#0288d1'];
                    return (
                      <Box
                        key={tagId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.5,
                          backgroundColor: 'rgba(0,0,0,0.05)',
                          borderRadius: 1,
                          fontSize: '0.75rem'
                        }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            backgroundColor: colors[index % colors.length],
                            borderRadius: 1
                          }}
                        />
                        <Typography variant="caption">
                          {tag ? tag.name : tagId}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Data Mode</InputLabel>
                <Select
                  value={properties.dataMode || 'simulation'}
                  label="Data Mode"
                  onChange={(e) => updateProperty('dataMode', e.target.value)}
                >
                  <MenuItem value="simulation">Simulation (Demo)</MenuItem>
                  <MenuItem value="historical">Historical Data (Static)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={4}>
              <TextField
                label="Max Data Points"
                type="number"
                value={properties.maxDataPoints || 50}
                onChange={(e) => updateProperty('maxDataPoints', parseInt(e.target.value) || 50)}
                size="small"
                fullWidth
                InputProps={{ inputProps: { min: 10, max: 1000 } }}
              />
            </Grid>
            
            <Grid item xs={4}>
              <TextField
                label="Refresh (ms)"
                type="number"
                value={properties.refreshInterval || 2000}
                onChange={(e) => updateProperty('refreshInterval', parseInt(e.target.value) || 1000)}
                size="small"
                fullWidth
                InputProps={{ inputProps: { min: 100, max: 10000 } }}
              />
            </Grid>
            
            <Grid item xs={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.showGrid !== undefined ? properties.showGrid : true}
                  onChange={(checked) => updateProperty('showGrid', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Show Grid</Typography>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.showLegend !== undefined ? properties.showLegend : true}
                  onChange={(checked) => updateProperty('showLegend', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Show Legend</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Y Axis Min"
                value={properties.yAxisMin || 'auto'}
                onChange={(e) => updateProperty('yAxisMin', e.target.value)}
                size="small"
                fullWidth
                placeholder="auto or number"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Y Axis Max"
                value={properties.yAxisMax || 'auto'}
                onChange={(e) => updateProperty('yAxisMax', e.target.value)}
                size="small"
                fullWidth
                placeholder="auto or number"
              />
            </Grid>
            
            {/* Line Colors Configuration */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Line Colors
              </Typography>
            </Grid>
            
            {[0, 1, 2, 3, 4].map(index => (
              <Grid item xs={6} key={index}>
                <TextField
                  label={`Line ${index + 1} Color`}
                  type="color"
                  value={(properties.lineColors && properties.lineColors[index]) || ['#1976d2', '#dc004e', '#2e7d32', '#ed6c02', '#0288d1'][index]}
                  onChange={(e) => {
                    const newColors = [...(properties.lineColors || ['#1976d2', '#dc004e', '#2e7d32', '#ed6c02', '#0288d1'])];
                    newColors[index] = e.target.value;
                    updateProperty('lineColors', newColors);
                  }}
                  size="small"
                  fullWidth
                  helperText={`Color for trend line ${index + 1}`}
                />
              </Grid>
            ))}
          </>
        )}

        {/* ComboBox Properties */}
        {(widget?.type === 'ComboBox' || widget?.component === 'ComboBox' || widget?.type === 'combo-box') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                ComboBox Properties
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Label"
                value={properties.label || ''}
                onChange={(e) => updateProperty('label', e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Options (JSON array)"
                value={optionsStringLocal}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  setOptionsStringLocal(inputValue);
                  
                  try {
                    const parsed = JSON.parse(inputValue);
                    updateProperty('options', parsed);
                  } catch (error) {
                    // Invalid JSON, but keep allowing editing
                  }
                }}
                size="small"
                fullWidth
                multiline
                rows={3}
                placeholder='["Option 1", "Option 2"] or [{"value":"val1","label":"Label 1"}]'
                error={(() => {
                  try {
                    JSON.parse(optionsStringLocal);
                    return false;
                  } catch {
                    return true;
                  }
                })()}
                helperText={(() => {
                  try {
                    JSON.parse(optionsStringLocal);
                    return 'Valid JSON format';
                  } catch (error) {
                    return `Invalid JSON: ${error.message}`;
                  }
                })()}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Selected Value"
                value={properties.selectedValue || ''}
                onChange={(e) => updateProperty('selectedValue', e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                fullWidth
                options={availableTags}
                getOptionLabel={(option) => `${option.name} (${option.type === 'tag' ? 'Tag' : 'Var'})`}
                value={availableTags.find(tag => {
                  const selectedValueTagIdValue = properties.selectedValueTagId;
                  if (typeof selectedValueTagIdValue === 'object' && selectedValueTagIdValue !== null) {
                    return tag.id === selectedValueTagIdValue.id;
                  }
                  return tag.id === selectedValueTagIdValue;
                }) || null}
                onChange={(e, newValue) => updateProperty('selectedValueTagId', newValue ? { type: newValue.type, id: newValue.id } : null)}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: option.type === 'tag' ? '#2196f3' : '#ff9800',
                          marginRight: 1,
                          flexShrink: 0
                        }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: option.type === 'var' ? 'bold' : 'normal' }}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description || 'No description'}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: option.type === 'tag' ? '#e3f2fd' : '#fff3e0',
                          color: option.type === 'tag' ? '#1565c0' : '#ef6c00',
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          textTransform: 'uppercase'
                        }}
                      >
                        {option.type}
                      </Box>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selected Value Tag"
                    placeholder="Tag to receive selected value..."
                    helperText="Tag that gets updated when user selects an option"
                  />
                )}
                noOptionsText="No tags available"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Placeholder"
                value={properties.placeholder || ''}
                onChange={(e) => updateProperty('placeholder', e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl size="small" fullWidth>
                <InputLabel>Variant</InputLabel>
                <Select
                  value={properties.variant || 'outlined'}
                  onChange={(e) => updateProperty('variant', e.target.value)}
                  label="Variant"
                >
                  <MenuItem value="outlined">Outlined</MenuItem>
                  <MenuItem value="filled">Filled</MenuItem>
                  <MenuItem value="standard">Standard</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl size="small" fullWidth>
                <InputLabel>Size</InputLabel>
                <Select
                  value={properties.size || 'medium'}
                  onChange={(e) => updateProperty('size', e.target.value)}
                  label="Size"
                >
                  <MenuItem value="small">Small</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReactSwitch
                  checked={properties.disabled || false}
                  onChange={(checked) => updateProperty('disabled', checked)}
                  onColor="#edbc45"
                  offColor="#ccc"
                  height={20}
                  width={40}
                  handleDiameter={16}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                <Typography variant="body2">Disabled</Typography>
              </Box>
            </Grid>
          </>
        )}

        {/* ButtonMenu Properties */}
        {(widget?.type === 'ButtonMenu' || widget?.component === 'ButtonMenu' || widget?.type === 'button-menu') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Button Menu Properties
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Text Alignment</InputLabel>
                <Select
                  value={properties.textAlign || 'center'}
                  onChange={(e) => updateProperty('textAlign', e.target.value)}
                  label="Text Alignment"
                >
                  <MenuItem value="left">Left</MenuItem>
                  <MenuItem value="center">Center</MenuItem>
                  <MenuItem value="right">Right</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Seçenekler ({(properties.options || []).length} adet)
                </Typography>
                {(properties.options || []).map((opt, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 0.5, mb: 0.5, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      label="Etiket"
                      value={opt.label || ''}
                      onChange={(e) => {
                        const newOptions = [...(properties.options || [])];
                        newOptions[idx] = { ...newOptions[idx], label: e.target.value };
                        updateProperty('options', newOptions);
                        setOptionsStringLocal(JSON.stringify(newOptions, null, 2));
                      }}
                      sx={{ flex: 2 }}
                    />
                    <TextField
                      size="small"
                      label="Değer"
                      value={opt.value || ''}
                      onChange={(e) => {
                        const newOptions = [...(properties.options || [])];
                        newOptions[idx] = { ...newOptions[idx], value: e.target.value };
                        updateProperty('options', newOptions);
                        setOptionsStringLocal(JSON.stringify(newOptions, null, 2));
                      }}
                      sx={{ flex: 1 }}
                    />
                    <input
                      type="color"
                      value={opt.color || '#ffffff'}
                      title="Arkaplan rengi"
                      onChange={(e) => {
                        const newOptions = [...(properties.options || [])];
                        newOptions[idx] = { ...newOptions[idx], color: e.target.value };
                        // colorMap'i de güncelle
                        const newColorMap = { ...(properties.colorMap || {}) };
                        newColorMap[newOptions[idx].value] = e.target.value;
                        updateProperty('options', newOptions);
                        updateProperty('colorMap', newColorMap);
                        setOptionsStringLocal(JSON.stringify(newOptions, null, 2));
                      }}
                      style={{ width: 32, height: 32, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
                    />
                    <input
                      type="color"
                      value={opt.textColor || '#000000'}
                      title="Yazı rengi"
                      onChange={(e) => {
                        const newOptions = [...(properties.options || [])];
                        newOptions[idx] = { ...newOptions[idx], textColor: e.target.value };
                        updateProperty('options', newOptions);
                        setOptionsStringLocal(JSON.stringify(newOptions, null, 2));
                      }}
                      style={{ width: 32, height: 32, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const newOptions = (properties.options || []).filter((_, i) => i !== idx);
                        updateProperty('options', newOptions);
                        setOptionsStringLocal(JSON.stringify(newOptions, null, 2));
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const newOptions = [...(properties.options || []), { label: 'Yeni', value: String((properties.options || []).length + 1), color: '#ffffff', textColor: '#000000' }];
                    updateProperty('options', newOptions);
                    setOptionsStringLocal(JSON.stringify(newOptions, null, 2));
                  }}
                  sx={{ mt: 0.5 }}
                >
                  Seçenek Ekle
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                size="small"
                fullWidth
                options={availableTags}
                getOptionLabel={(option) => `${option.name} (${option.type === 'tag' ? 'Tag' : 'Var'})`}
                value={availableTags.find(tag => tag.id === properties.tagId) || null}
                onChange={(e, newValue) => updateProperty('tagId', newValue?.id || null)}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: option.type === 'tag' ? '#2196f3' : '#ff9800',
                          marginRight: 1,
                          flexShrink: 0
                        }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: option.type === 'var' ? 'bold' : 'normal' }}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description || 'No description'}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: option.type === 'tag' ? '#e3f2fd' : '#fff3e0',
                          color: option.type === 'tag' ? '#1565c0' : '#ef6c00',
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          textTransform: 'uppercase'
                        }}
                      >
                        {option.type}
                      </Box>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tag"
                    placeholder="Select tag to control..."
                    helperText="Tag that gets updated when user selects an option"
                  />
                )}
                noOptionsText="No tags available"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Text Color"
                type="color"
                value={properties.textColor || '#000000'}
                onChange={(e) => updateProperty('textColor', e.target.value)}
                size="small"
                fullWidth
                helperText="Button text color"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Default Color"
                type="color"
                value={properties.defaultColor || '#ffffff'}
                onChange={(e) => updateProperty('defaultColor', e.target.value)}
                size="small"
                fullWidth
                helperText="Fallback button color"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Note: OFF or '0' values will show white background, all other values will show green background by default.
              </Typography>
            </Grid>
          </>
        )}

        {/* Line Widget Properties */}
        {(widget?.type === 'Line' || widget?.component === 'Line' || widget?.type === 'line') && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Line Properties
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl size="small" fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={properties.direction || 'horizontal'}
                  onChange={(e) => updateProperty('direction', e.target.value)}
                  label="Direction"
                >
                  <MenuItem value="horizontal">Horizontal</MenuItem>
                  <MenuItem value="vertical">Vertical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {/* Widget Specific Properties */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            Widget Properties
          </Typography>
        </Grid>
        
        {Object.entries(properties)
          .filter(([key]) => !['x', 'y', 'width', 'height', 'backgroundColor', 'foreColor', 'borderWidth', 'borderColor', 'borderRadius', 'fontSize', 'fontWeight', 'fontFamily', 'opacity', 'tagId', 'textAlign', 'startAngle', 'endAngle', 'min', 'max', 'showZones', 'normalZoneEnd', 'warningZoneEnd', 'unit', 'title', 'actionType', 'targetTagId', 'tagOperation', 'tagSetValue', 'tagIncrementValue', 'navigateTo', 'openInNewTab', 'taskCommand', 'taskParameters', 'action', 'text', 'variant', 'disabled', 'onClick', 'tagValue', 'value', 'placeholder', 'readOnly', 'onChange', 'color', 'warningThreshold', 'dangerThreshold', 'forecolor', 'backcolor', 'showValue', 'vertical', 'circular', 'thickness', 'src', 'alt', 'fit', 'label', 'checked', 'size', 'maxDataPoints', 'refreshInterval', 'showGrid', 'showLegend', 'yAxisMin', 'yAxisMax', 'options', 'selectedValue', 'direction', 'tagIds', 'trendTagId', 'selectedValueTagId', 'dataMode', 'customComponents', 'customCanvasSize', 'customCanvasProps', 'metadata', 'numberFormat'].includes(key) && !key.startsWith('input_') && !key.startsWith('output_'))
          .map(([key, value]) => (
            <Grid item xs={12} key={key}>
              {renderPropertyField(key, value)}
            </Grid>
          ))}
        
        {/* Delete Widget */}
        <Grid item xs={12}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            fullWidth
            onClick={() => onDelete && onDelete(widget.id)}
            sx={{ mt: 2 }}
          >
            Delete Widget
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SimplePropertyInspector;